/**
 * Relative time utilities for displaying "time ago" strings
 */

/**
 * Calculate relative time from ISO date string
 * @example getRelativeTime("2024-10-07T12:00:00Z") => "2m ago"
 */
export function getRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'just now';
  }
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks}w ago`;
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months}mo ago`;
  }
  const years = Math.floor(diffDays / 365);
  return `${years}y ago`;
}

/**
 * Check if timestamp is within last N minutes
 * @example isWithinMinutes("2024-10-07T12:00:00Z", 5) => true/false
 */
export function isWithinMinutes(dateString: string, minutes: number): boolean {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = diffMs / (1000 * 60);
  return diffMinutes <= minutes;
}

/**
 * Check if timestamp has expired
 * @example isExpired("2024-10-07T12:00:00Z") => true/false
 */
export function isExpired(dateString: string): boolean {
  const date = new Date(dateString);
  const now = new Date();
  return date.getTime() < now.getTime();
}

/**
 * Get time remaining until expiration
 * @example getTimeRemaining("2024-10-07T12:00:00Z") => "5m 30s"
 */
export function getTimeRemaining(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();

  if (diffMs <= 0) {
    return 'expired';
  }

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return `${diffDays}d`;
  }
  if (diffHours > 0) {
    return `${diffHours}h`;
  }
  if (diffMinutes > 0) {
    const remainingSeconds = diffSeconds % 60;
    return remainingSeconds > 0 ? `${diffMinutes}m ${remainingSeconds}s` : `${diffMinutes}m`;
  }
  return `${diffSeconds}s`;
}
